import * as THREE from "three";
import { AnimationPlayer } from "./animationPlayer";
import { loadPackage, type LoadedHuman } from "./assetLoader";
import { BoneColliders } from "./boneColliders";
import { Clock } from "./clock";
import { PhysicsWorld, initRapier } from "./physicsWorld";
import { PoseDriver } from "./poseDriver";
import { PoseInteract } from "./poseInteract";
import { PointLayers } from "./pointLayers";
import { budgetsFor, resolveQualityTier, writeStoredTier } from "./quality";
import {
  aerialSpan,
  cameraPresetPose,
  instanceOffset,
  type CameraPreset,
  type CompareLayout,
  type ViewMode,
} from "./compareView";
import { createSceneGraph, type SceneGraph } from "./sceneGraph";
import { applyPose as applyExportedPose, exportPose as exportHumansPose, type ExportedPose } from "./poseExport";
import { ForceOverlay3D } from "./biomech/forceOverlay3d.ts";
import { RealtimeEstimator, validateFrames } from "./biomech/estimator.ts";
import { MujocoLiveSolver } from "./biomech/mujocoSolver.ts";
import { BiomechTrackPlayer } from "./biomech/trackPlayer.ts";
import { guessRole, mapBoneNames, pointsFromBones, type BonePosition } from "./biomech/boneMapping.ts";
import { DEFAULT_OVERLAY_OPTIONS } from "./biomech/types.ts";
import type {
  BiomechFrame,
  CanonicalPoint,
  EstimatorPersonInput,
  OverlayOptions,
  OverlayText,
  PoseTrack,
  ValidationSummary,
  Vec3,
} from "./biomech/types.ts";
import type {
  AttachOptions,
  DotNetBridge,
  DriveMode,
  PackageManifest,
  QualityTier,
  RepresentationMode,
} from "./types";

interface RuntimeState {
  canvas: HTMLCanvasElement;
  graph: SceneGraph;
  clock: Clock;
  anim: AnimationPlayer;
  pose: PoseDriver;
  interact: PoseInteract;
  physics: PhysicsWorld | null;
  colliders: BoneColliders;
  pointLayers: PointLayers;
  physicsInit: Promise<void> | null;
  orbitActive: boolean;
  forceFrames: number;
  lastOnTimeTs: number;
  tier: QualityTier;
  representation: RepresentationMode;
  driveMode: DriveMode;
  humans: LoadedHuman[];
  lastManifest: PackageManifest | null;
  compareLayers: { id: string; offset: number; duration: number }[];
  compareLayout: CompareLayout;
  cameraPreset: CameraPreset;
  viewMode: ViewMode;
  quadCameras: {
    aerial: THREE.PerspectiveCamera;
    front: THREE.PerspectiveCamera;
    side: THREE.PerspectiveCamera;
    iso: THREE.PerspectiveCamera;
  } | null;
  materialOriginals: WeakMap<THREE.Material, boolean>;
  clonedMaterials: Set<THREE.Material>;
  biomech: BiomechState | null;
  raf: number;
  lastTs: number;
  disposed: boolean;
  /** Last playing flag reported to DotNet (detect auto-stop at end). */
  lastReportedPlaying: boolean;
  dotNet?: DotNetBridge | null;
  onResize: () => void;
}

const handles = new WeakMap<HTMLCanvasElement, RuntimeState>();

function requireState(canvas: HTMLCanvasElement): RuntimeState {
  const state = handles.get(canvas);
  if (!state || state.disposed) {
    throw new Error("acroHumanScene: call attach first");
  }
  return state;
}

/* ------------------------------------------------------------- biomech */

type ForceSolver = "estimate" | "mujoco";

interface BiomechState {
  enabled: boolean;
  source: "track" | "realtime";
  /** User-selected realtime solver. Track playback ignores this. */
  solver: ForceSolver;
  /** Method actually producing realtime frames (`estimate` while MuJoCo is loading or has failed). */
  liveMethod: ForceSolver;
  /** Stable failure code for the host. Null when the live solver is healthy or not selected. */
  error: string | null;
  mujocoWarned: boolean;
  mujoco: MujocoLiveSolver;
  overlay: ForceOverlay3D;
  player: BiomechTrackPlayer | null;
  /**
   * One realtime estimator per compare layer: persons only interact (contacts, load sharing)
   * with persons of their own layer — never with a ghost of another take in the same spot.
   */
  estimators: Map<string, RealtimeEstimator>;
  options: OverlayOptions;
  lastFrame: BiomechFrame | null;
  recent: BiomechFrame[];
  liveValidation: ValidationSummary | null;
  lastValidationAt: number;
  lastStateAt: number;
  lastStateKey: string;
  /** Clock time of the last streaming push (static solve when time has not advanced). */
  lastPushTime: number | null;
  masses: Map<number, number>;
  boneCache: WeakMap<THREE.Object3D, { bones: THREE.Bone[]; mapping: Partial<Record<CanonicalPoint, string>> }>;
}

const BIOMECH_VALIDATION_WINDOW = 90;
const BIOMECH_STATE_INTERVAL_MS = 250;
const tmpBonePos = new THREE.Vector3();

function ensureBiomech(state: RuntimeState): BiomechState {
  if (state.biomech) return state.biomech;
  state.biomech = {
    enabled: false,
    source: "realtime",
    solver: "estimate",
    liveMethod: "estimate",
    error: null,
    mujocoWarned: false,
    mujoco: new MujocoLiveSolver(),
    overlay: new ForceOverlay3D(state.graph.scene),
    player: null,
    estimators: new Map(),
    options: { ...DEFAULT_OVERLAY_OPTIONS },
    lastFrame: null,
    recent: [],
    liveValidation: null,
    lastValidationAt: 0,
    lastStateAt: 0,
    lastStateKey: "",
    lastPushTime: null,
    masses: new Map(),
    boneCache: new WeakMap(),
  };
  return state.biomech;
}

/** Scene floor height (human-scene floors are flat planes; default y = 0). */
function sceneFloorY(state: RuntimeState): number {
  const y = state.graph.floor?.position?.y;
  return typeof y === "number" && Number.isFinite(y) ? y : 0;
}

/** Estimator for one compare layer, pinned to the scene floor (no auto-floor in 3D). */
function estimatorFor(state: RuntimeState, biomech: BiomechState, layer: string): RealtimeEstimator {
  let est = biomech.estimators.get(layer);
  if (!est) {
    est = new RealtimeEstimator({ floorY: sceneFloorY(state) });
    biomech.estimators.set(layer, est);
  } else {
    est.setFloorY(sceneFloorY(state));
  }
  return est;
}

function resetBiomechEstimators(biomech: BiomechState): void {
  for (const est of biomech.estimators.values()) est.reset();
  biomech.estimators.clear();
  biomech.mujoco.reset();
  biomech.recent.length = 0;
  biomech.lastFrame = null;
  biomech.liveValidation = null;
  biomech.lastPushTime = null;
}

/** Bones + cached canonical-point mapping for one loaded human. */
function humanBoneData(
  biomech: BiomechState,
  human: LoadedHuman,
): { bones: THREE.Bone[]; mapping: Partial<Record<CanonicalPoint, string>> } {
  const cached = biomech.boneCache.get(human.root);
  if (cached) return cached;
  const bones: THREE.Bone[] = [];
  human.root.traverse((obj) => {
    if (obj instanceof THREE.Bone) bones.push(obj);
  });
  const data = { bones, mapping: mapBoneNames(bones.map((b) => b.name)) };
  biomech.boneCache.set(human.root, data);
  return data;
}

interface ScenePerson {
  /** Compare layer id ("" for a single package). */
  layer: string;
  person: EstimatorPersonInput;
}

/** Canonical points (scene metres, Y-up) for every loaded human, tagged with its layer. */
function collectScenePersons(state: RuntimeState): ScenePerson[] {
  const biomech = ensureBiomech(state);
  const persons: ScenePerson[] = [];
  for (let i = 0; i < state.humans.length; i++) {
    const human = state.humans[i]!;
    if (!human.root.visible) continue;
    human.root.updateWorldMatrix(true, true);
    const { bones, mapping } = humanBoneData(biomech, human);
    if (bones.length === 0) continue;
    const positions: BonePosition[] = bones.map((bone) => {
      bone.getWorldPosition(tmpBonePos);
      return { name: bone.name, position: [tmpBonePos.x, tmpBonePos.y, tmpBonePos.z] as Vec3 };
    });
    const points = pointsFromBones(positions, mapping);
    if (Object.keys(points).length < 6) continue;
    persons.push({
      layer: human.layerId ?? "",
      person: {
        id: i,
        role: guessRole(human.def.id),
        points,
        massKg: null,
        heightM: null,
      },
    });
  }
  return persons;
}

function biomechBodyWeights(biomech: BiomechState, frame: BiomechFrame | null): Map<number, number> {
  const out = new Map<number, number>();
  for (const person of biomech.player?.persons ?? []) {
    out.set(person.id, (person.massKg || 70) * 9.81);
  }
  for (const [id, mass] of biomech.masses) out.set(id, mass * 9.81);
  for (const person of frame?.persons ?? []) {
    if (!out.has(person.id)) out.set(person.id, 70 * 9.81);
  }
  return out;
}

/** Realtime estimate for the current scene: streaming while time advances, else quasi-static. */
function solveSceneRealtime(state: RuntimeState, biomech: BiomechState): BiomechFrame | null {
  const scene = collectScenePersons(state);
  if (scene.length === 0) return null;
  const t = state.clock.time;
  // Paused, scrub-held, or posing: the clock does not advance, so there are no derivatives —
  // solve the pose quasi-statically instead of waiting for a window that never fills.
  const advancing =
    state.clock.playing && (biomech.lastPushTime == null || t > biomech.lastPushTime + 1e-6);

  const byLayer = new Map<string, EstimatorPersonInput[]>();
  for (const { layer, person } of scene) {
    const list = byLayer.get(layer) ?? [];
    list.push(person);
    byLayer.set(layer, list);
  }
  const merged: BiomechFrame = { t, persons: [], contacts: [] };
  let any = false;
  for (const [layer, persons] of byLayer) {
    const est = estimatorFor(state, biomech, layer);
    const frame = advancing ? est.push(t, persons) : est.solveStatic(t, persons);
    const extras = est.lastExtras;
    if (extras) {
      for (const [id, mass] of extras.masses) biomech.masses.set(id, mass);
    }
    if (!frame) continue;
    any = true;
    merged.t = frame.t;
    merged.persons.push(...frame.persons);
    merged.contacts.push(...frame.contacts);
  }
  if (advancing) biomech.lastPushTime = t;
  return any ? merged : null;
}

function setLiveMethod(biomech: BiomechState, method: ForceSolver): void {
  if (biomech.liveMethod === method) return;
  biomech.recent.length = 0;
  biomech.liveValidation = null;
  biomech.liveMethod = method;
}

/** One console warning plus `error: "mujoco-failed"` until a MuJoCo frame succeeds. */
function noteMujocoFailure(biomech: BiomechState, err: unknown): void {
  setLiveMethod(biomech, "estimate");
  biomech.error = "mujoco-failed";
  if (biomech.mujocoWarned) return;
  biomech.mujocoWarned = true;
  console.warn("acroHumanScene: mujoco-failed", err);
}

function pushRealtimeFrame(
  state: RuntimeState,
  biomech: BiomechState,
  frame: BiomechFrame,
  ts: number,
  method: ForceSolver,
): void {
  setLiveMethod(biomech, method);
  biomech.recent.push(frame);
  if (biomech.recent.length > BIOMECH_VALIDATION_WINDOW) biomech.recent.shift();
  const masses = () => [...biomech.masses].map(([id, massKg]) => ({ id, massKg }));
  if (ts - biomech.lastValidationAt > 1000 && biomech.recent.length > 5) {
    biomech.lastValidationAt = ts;
    biomech.liveValidation = validateFrames(biomech.recent, masses(), {
      method,
      floorY: sceneFloorY(state),
    });
  } else if (!biomech.liveValidation) {
    biomech.liveValidation = validateFrames([frame], masses(), {
      method,
      floorY: sceneFloorY(state),
    });
  }
}

/** Inverse dynamics for the current scene. Falls back to the estimator on init/step failure. */
function solveSceneMujoco(state: RuntimeState, biomech: BiomechState): {
  frame: BiomechFrame | null;
  method: ForceSolver | null;
} {
  const scene = collectScenePersons(state);
  if (scene.length === 0) return { frame: null, method: null };
  const solver = biomech.mujoco;
  if (solver.failed) {
    noteMujocoFailure(biomech, "mujoco-failed");
    return { frame: solveSceneRealtime(state, biomech), method: "estimate" };
  }
  if (!solver.ready) {
    void solver.ensureLoaded().catch((err) => {
      if (state.disposed) return;
      noteMujocoFailure(biomech, err);
      emitBiomechState(state, biomech);
    });
    return { frame: solveSceneRealtime(state, biomech), method: "estimate" };
  }
  try {
    const frame = solveMujocoLayers(state, biomech, scene);
    if (!frame) return { frame: null, method: null };
    biomech.error = null;
    biomech.mujocoWarned = false;
    return { frame, method: "mujoco" };
  } catch (err) {
    noteMujocoFailure(biomech, err);
    return { frame: solveSceneRealtime(state, biomech), method: "estimate" };
  }
}

function solveMujocoLayers(state: RuntimeState, biomech: BiomechState, scene: ScenePerson[]): BiomechFrame | null {
  const t = state.clock.time;
  const advancing =
    state.clock.playing && (biomech.lastPushTime == null || t > biomech.lastPushTime + 1e-6);
  const byLayer = new Map<string, EstimatorPersonInput[]>();
  for (const { layer, person } of scene) {
    const list = byLayer.get(layer) ?? [];
    list.push(person);
    byLayer.set(layer, list);
  }
  const solver = biomech.mujoco;
  solver.lastMasses.clear();
  const merged: BiomechFrame = { t, persons: [], contacts: [] };
  let any = false;
  for (const [layer, persons] of byLayer) {
    const part = solver.solve(layer, t, persons, { held: !advancing, floorY: sceneFloorY(state) });
    if (!part) continue;
    any = true;
    merged.t = part.t;
    merged.persons.push(...part.persons);
    merged.contacts.push(...part.contacts);
  }
  for (const [id, mass] of solver.lastMasses) biomech.masses.set(id, mass);
  if (advancing) biomech.lastPushTime = t;
  return any ? merged : null;
}

/** Per-frame hook: solve/sample a biomech frame and refresh the 3D overlay. */
function updateBiomech(state: RuntimeState, ts: number): void {
  const biomech = state.biomech;
  if (!biomech || !biomech.enabled) return;

  let frame: BiomechFrame | null = null;
  if (biomech.source === "track") {
    if (biomech.error === "mujoco-failed") biomech.error = null;
    frame = biomech.player ? biomech.player.sample(state.clock.time) : null;
  } else if (biomech.solver === "mujoco") {
    const live = solveSceneMujoco(state, biomech);
    frame = live.frame;
    if (frame && live.method) pushRealtimeFrame(state, biomech, frame, ts, live.method);
  } else {
    if (biomech.error === "mujoco-failed") biomech.error = null;
    frame = solveSceneRealtime(state, biomech);
    if (frame) pushRealtimeFrame(state, biomech, frame, ts, "estimate");
  }
  if (frame) biomech.lastFrame = frame;
  biomech.overlay.update(frame ?? biomech.lastFrame, biomechBodyWeights(biomech, frame));

  if (state.dotNet && ts - biomech.lastStateAt >= BIOMECH_STATE_INTERVAL_MS) {
    biomech.lastStateAt = ts;
    emitBiomechState(state, biomech);
  }
}

function biomechFrameSummary(frame: BiomechFrame | null): {
  t: number;
  personCount: number;
  contactCount: number;
  maxLoad: number;
  maxResidualN: number;
} | null {
  if (!frame) return null;
  let maxLoad = 0;
  let maxResidualN = 0;
  for (const person of frame.persons) {
    for (const joint of Object.values(person.joints)) {
      if (joint) maxLoad = Math.max(maxLoad, joint.load);
    }
    maxResidualN = Math.max(maxResidualN, Math.hypot(...person.residual.f));
  }
  return {
    t: frame.t,
    personCount: frame.persons.length,
    contactCount: frame.contacts.length,
    maxLoad,
    maxResidualN,
  };
}

function currentValidation(biomech: BiomechState): ValidationSummary | null {
  return biomech.source === "track" ? biomech.player?.validation ?? null : biomech.liveValidation;
}

function biomechState(state: RuntimeState, biomech: BiomechState): Record<string, unknown> {
  void state;
  const validation = currentValidation(biomech);
  return {
    // `validation` is what the Blazor state parser reads; `liveValidation` is the same object.
    validation,
    enabled: biomech.enabled,
    source: biomech.source,
    method: biomech.source === "track" ? biomech.player?.method ?? "estimate" : biomech.liveMethod,
    solver: biomech.solver,
    error: biomech.error,
    hasTrack: !!biomech.player,
    options: {
      forces: biomech.options.forces,
      torques: biomech.options.torques,
      contacts: biomech.options.contacts,
      labels: biomech.options.labels,
      residual: biomech.options.residual,
    },
    frame: biomechFrameSummary(biomech.lastFrame),
    liveValidation: validation,
  };
}

/** What the host renders from the state (per-frame numbers deliberately excluded). */
function biomechStateKey(biomech: BiomechState): string {
  const v = currentValidation(biomech);
  return JSON.stringify([
    biomech.enabled,
    biomech.source,
    biomech.solver,
    !!biomech.player,
    biomech.player?.method ?? "estimate",
    biomech.liveMethod,
    biomech.error,
    v
      ? [
          v.status,
          Math.round(v.score ?? 0),
          v.method,
          (v.findings ?? []).map((f) => [f.code, f.severity, Math.round((f.startTime ?? 0) * 10)]),
        ]
      : null,
  ]);
}

/**
 * OnBiomechState is optional on the C# side — never let a missing method break the loop.
 * Only sent when something the host shows actually changed (no 4 Hz re-render storm).
 */
function emitBiomechState(state: RuntimeState, biomech: BiomechState): void {
  if (!state.dotNet) return;
  const key = biomechStateKey(biomech);
  if (key === biomech.lastStateKey) return;
  biomech.lastStateKey = key;
  try {
    void state.dotNet
      .invokeMethodAsync("OnBiomechState", JSON.stringify(biomechState(state, biomech)))
      .catch(() => {});
  } catch {
    /* method not implemented by the component */
  }
}

/** Drop cached bones / filter history / the previous scene's track when the scene changes. */
function resetBiomech(state: RuntimeState): void {
  const biomech = state.biomech;
  if (!biomech) return;
  resetBiomechEstimators(biomech);
  // A baked track belongs to the scene it was analysed for; the host re-sends it if still valid.
  biomech.player = null;
  biomech.masses.clear();
  biomech.boneCache = new WeakMap();
  biomech.overlay.clear();
  biomech.lastStateKey = "";
}

export interface ForceOverlayInput {
  enabled?: boolean;
  source?: "track" | "realtime";
  /** Realtime solver. Omitted or "estimate" keeps today's estimator. "mujoco" is inverse dynamics. */
  solver?: "estimate" | "mujoco";
  /** BiomechTrack v1 — JSON string (preferred) or object; null clears, undefined keeps. */
  track?: unknown;
  forces?: boolean;
  torques?: boolean;
  contacts?: boolean;
  labels?: boolean;
  residual?: boolean;
  /** Localized label / legend strings. */
  text?: OverlayText | null;
}

/** Enable/configure the 3D joint-force overlay. */
function setForceOverlay(canvas: HTMLCanvasElement, opts: ForceOverlayInput): Record<string, unknown> {
  const state = requireState(canvas);
  const biomech = ensureBiomech(state);
  const options = opts ?? {};

  if (options.track !== undefined) {
    biomech.player = options.track == null ? null : BiomechTrackPlayer.parse(options.track);
    if (options.track != null && !biomech.player) {
      console.warn("acroHumanScene: not a biomech track (acro.biomech.track v1)");
    }
    biomech.lastFrame = null;
  }
  if (options.source === "track" || options.source === "realtime") {
    if (biomech.source !== options.source) {
      biomech.lastFrame = null;
      if (options.source === "realtime") resetBiomechEstimators(biomech);
    }
    biomech.source = options.source;
  }
  if (options.solver === "mujoco" || options.solver === "estimate") {
    biomech.solver = options.solver;
    if (options.solver === "mujoco") {
      biomech.mujoco.retry();
      biomech.error = null;
      biomech.mujocoWarned = false;
      if (!biomech.mujoco.ready && !biomech.mujoco.failed) {
        void biomech.mujoco.ensureLoaded().catch((err) => {
          if (state.disposed) return;
          noteMujocoFailure(biomech, err);
          emitBiomechState(state, biomech);
        });
      }
    } else if (biomech.error === "mujoco-failed") {
      biomech.error = null;
    }
  }
  for (const key of ["forces", "torques", "contacts", "labels", "residual"] as const) {
    if (typeof options[key] === "boolean") biomech.options[key] = options[key] as boolean;
  }
  if (options.text !== undefined) biomech.options.text = options.text ?? undefined;
  biomech.overlay.setOptions({ ...biomech.options, text: biomech.options.text ?? {} });

  if (typeof options.enabled === "boolean") {
    biomech.enabled = options.enabled;
    if (!biomech.enabled) {
      biomech.overlay.clear();
      resetBiomechEstimators(biomech);
    }
  }
  biomech.overlay.setVisible(biomech.enabled);
  state.forceFrames = Math.max(state.forceFrames, 3);
  kick(state);
  emitBiomechState(state, biomech);
  return biomechState(state, biomech);
}

function getForceOverlayState(canvas: HTMLCanvasElement): Record<string, unknown> {
  const state = requireState(canvas);
  const biomech = ensureBiomech(state);
  return biomechState(state, biomech);
}

/* ------------------------------------------------- pose track sampling */

/** Upload budget: the API caps a PoseTrack at 8 MiB; stay below it with escaping overhead. */
const POSE_TRACK_MAX_CHARS = 7_500_000;
const POSE_TRACK_DEFAULT_FPS = 15;
const POSE_TRACK_MIN_FPS = 5;
const POSE_TRACK_MAX_FRAMES = 5400;
/** Frames sampled per synchronous batch before yielding to the event loop. */
const POSE_TRACK_CHUNK = 8;

interface TransformSnapshot {
  obj: THREE.Object3D;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  scale: THREE.Vector3;
}

/** Local transforms of every node under the human root (bones and animated nodes). */
function snapshotTransforms(root: THREE.Object3D): TransformSnapshot[] {
  const out: TransformSnapshot[] = [];
  root.traverse((obj) => {
    out.push({
      obj,
      position: obj.position.clone(),
      quaternion: obj.quaternion.clone(),
      scale: obj.scale.clone(),
    });
  });
  return out;
}

function restoreTransforms(root: THREE.Object3D, snapshot: TransformSnapshot[]): void {
  for (const s of snapshot) {
    s.obj.position.copy(s.position);
    s.obj.quaternion.copy(s.quaternion);
    s.obj.scale.copy(s.scale);
  }
  root.updateMatrixWorld(true);
}

type PoseTrackFrame = PoseTrack["frames"][number];

/**
 * Sample scene poses at the given times without disturbing what the user sees: clock, mixer
 * times and every node's local transform are saved and restored around the batch, so pose
 * edits (pose mode / dirty pose) and ragdoll bones survive, and physics bodies are never
 * re-synced from sampled bones (the render loop only runs between batches, on restored state).
 */
function sampleScenePoses(
  state: RuntimeState,
  times: number[],
  roles: Map<number, string>,
  heights: Map<number, number>,
): PoseTrackFrame[] {
  const root = state.graph.humanRoot;
  const snapshot = snapshotTransforms(root);
  const savedTime = state.clock.time;
  const savedPlaying = state.clock.playing;
  const frames: PoseTrackFrame[] = [];
  try {
    state.clock.playing = false;
    for (const t of times) {
      state.clock.time = t;
      state.anim.snapToClock(state.clock);
      frames.push({
        t: round3(t),
        persons: collectScenePersons(state).map(({ person }) => {
          roles.set(person.id, person.role ?? "base");
          const landmarks: Record<string, number[]> = {};
          let lo = Infinity;
          let hi = -Infinity;
          for (const [name, p] of Object.entries(person.points)) {
            if (!p) continue;
            landmarks[name] = [round3(p[0]), round3(p[1]), round3(p[2]), 1];
            lo = Math.min(lo, p[1]);
            hi = Math.max(hi, p[1]);
          }
          if (Number.isFinite(hi - lo) && hi - lo > 0.5) {
            heights.set(person.id, Math.max(heights.get(person.id) ?? 0, (hi - lo) * 1.05));
          }
          return { id: person.id, root: null, landmarks };
        }),
      });
    }
  } finally {
    state.clock.time = savedTime;
    state.clock.playing = savedPlaying;
    // Re-align mixer action times with the real clock (absolute; bones are overwritten right
    // after), then put back the exact transforms that were on screen — posed and
    // ragdoll-owned bones included.
    state.anim.snapToClock(state.clock);
    restoreTransforms(root, snapshot);
  }
  return frames;
}

function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export interface PoseTrackSampleResult {
  /** PoseTrack v1 JSON (a string, so the host never re-serializes multi-MB documents). */
  json: string;
  fps: number;
  frameCount: number;
  sourceDuration: number;
  sampledDuration: number;
  /** Duration had to be cut to fit the upload budget. */
  trimmed: boolean;
  /** Frame rate was lowered below the requested rate to fit the budget. */
  reducedFps: boolean;
}

/**
 * Sample the loaded animation into a PoseTrack v1 for upload/offline analysis.
 * Chunked (yields between batches), 15 fps by default, and budgeted to < 7.5 MB: the frame
 * rate drops towards 5 fps first, then the duration is trimmed (flagged in the result).
 */
async function sampleBiomechPoseTrackJson(
  canvas: HTMLCanvasElement,
  fps = POSE_TRACK_DEFAULT_FPS,
): Promise<PoseTrackSampleResult> {
  const state = requireState(canvas);
  const requested =
    Number.isFinite(fps) && fps > 0 ? Math.min(60, Math.max(1, fps)) : POSE_TRACK_DEFAULT_FPS;
  const duration = Math.max(0, state.clock.duration);
  const roles = new Map<number, string>();
  const heights = new Map<number, number>();

  // Size probe: a couple of representative frames → characters per frame.
  const probe = sampleScenePoses(state, duration > 0 ? [0, duration / 2] : [0], new Map(), new Map());
  const perFrame = Math.max(64, ...probe.map((f) => JSON.stringify(f).length + 1));
  const headerBudget = 2048;
  const cap = Math.min(
    POSE_TRACK_MAX_FRAMES,
    Math.max(1, Math.floor((POSE_TRACK_MAX_CHARS - headerBudget) / perFrame)),
  );
  const framesAt = (rate: number) => Math.floor(duration * rate) + 1;

  let rate = requested;
  for (const candidate of [requested, 12, 10, 8, 6, POSE_TRACK_MIN_FPS]) {
    if (candidate > requested) continue;
    rate = candidate;
    if (framesAt(candidate) <= cap) break;
  }
  let frameCount = framesAt(rate);
  let trimmed = false;
  if (frameCount > cap) {
    frameCount = cap;
    trimmed = true;
  }

  const step = 1 / rate;
  const frames: PoseTrackFrame[] = [];
  for (let start = 0; start < frameCount; start += POSE_TRACK_CHUNK) {
    const times: number[] = [];
    for (let i = start; i < Math.min(frameCount, start + POSE_TRACK_CHUNK); i++) {
      times.push(Math.min(duration, i * step));
    }
    frames.push(...sampleScenePoses(state, times, roles, heights));
    if (start + POSE_TRACK_CHUNK < frameCount) {
      await yieldToEventLoop();
      if (state.disposed) throw new Error("acroHumanScene: disposed while sampling");
    }
  }
  state.forceFrames = Math.max(state.forceFrames, 2);
  kick(state);

  const personIds = [...roles.keys()].sort((a, b) => a - b);
  const build = (): PoseTrack => ({
    format: "acro.pose.track",
    version: 1,
    source: "scene",
    space: "y-up-m",
    fps: rate,
    persons: personIds.map((id) => ({
      id,
      role: roles.get(id) ?? "base",
      heightM: heights.has(id) ? round3(heights.get(id)!) : null,
      massKg: null,
    })),
    frames,
  });
  let json = JSON.stringify(build());
  // The estimate was per frame; enforce the real size (drop tail frames until it fits).
  while (json.length > POSE_TRACK_MAX_CHARS && frames.length > 1) {
    frames.length = Math.max(1, Math.floor(frames.length * 0.9));
    trimmed = true;
    json = JSON.stringify(build());
  }
  const last = frames[frames.length - 1];
  return {
    json,
    fps: rate,
    frameCount: frames.length,
    sourceDuration: round3(duration),
    sampledDuration: round3(last ? last.t : 0),
    trimmed,
    reducedFps: rate < requested,
  };
}

/**
 * Legacy entry (hosts from before sampleBiomechPoseTrackJson): same sampling, returns the
 * PoseTrack object. Async now — Blazor's InvokeAsync awaits the promise transparently.
 */
async function sampleBiomechPoseTrack(
  canvas: HTMLCanvasElement,
  fps = POSE_TRACK_DEFAULT_FPS,
): Promise<PoseTrack> {
  const result = await sampleBiomechPoseTrackJson(canvas, fps);
  return JSON.parse(result.json) as PoseTrack;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function applyRepresentation(state: RuntimeState, mode: RepresentationMode): void {
  state.representation = mode;
  const wire = mode === "wireframe" || mode === "solid+wireframe" || mode === "points";
  const solidVisible = mode === "solid" || mode === "solid+wireframe" || mode === "wireframe";
  // points: wireframe silhouette on meshes + dense/joint point-layer overlay (H4).

  state.graph.humanRoot.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;

    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const next: THREE.Material[] = [];
    for (const mat of materials) {
      let m = mat;
      if (!state.materialOriginals.has(mat)) {
        // Clone shared materials so wireframe does not leak across instances.
        m = mat.clone();
        state.clonedMaterials.add(m);
        state.materialOriginals.set(mat, true);
        state.materialOriginals.set(m, true);
      }
      if ("wireframe" in m) {
        (m as THREE.MeshStandardMaterial).wireframe = wire;
      }
      m.visible = solidVisible || mode === "points";
      next.push(m);
    }
    mesh.material = Array.isArray(mesh.material) ? next : next[0];
    mesh.visible = solidVisible || mode === "points";
  });

  state.pointLayers.setRepresentation(mode);
}

function reportTime(state: RuntimeState): void {
  if (!state.dotNet) return;
  state.dotNet
    .invokeMethodAsync("OnTime", state.clock.time, state.clock.playing)
    .catch(() => {});
  state.lastReportedPlaying = state.clock.playing;
}

function kick(state: RuntimeState): void {
  if (state.disposed) return;
  if (state.raf) return;
  state.raf = requestAnimationFrame((t) => frame(state, t));
}

function needsFrame(state: RuntimeState): boolean {
  return (
    state.clock.playing ||
    state.interact.isDragging() ||
    state.orbitActive ||
    state.forceFrames > 0
  );
}

function frame(state: RuntimeState, ts: number): void {
  if (state.disposed) return;
  state.raf = 0;
  const dt = state.lastTs ? Math.min(0.05, (ts - state.lastTs) / 1000) : 0;
  state.lastTs = ts;

  const wasPlaying = state.clock.playing;
  state.clock.tick(dt);

  // Pose mode / paused dirty pose: do not let AnimationPlayer overwrite joints.
  // H3 ragdoll: still animate non-dynamic humans (tier low keeps #2 kinematic).
  if (state.pose.shouldApplyAnimation(state.clock.playing)) {
    state.anim.update(state.clock, dt);
  } else if (state.driveMode === "ragdoll") {
    const exclude = new Set(state.colliders.getDynamicHumanIds());
    state.anim.update(state.clock, dt, exclude);
  } else if (state.driveMode === "pose") {
    state.interact.syncPickers();
  }

  // H2/H3: fixed-step physics; ragdoll writes dynamic bones after step.
  state.pose.updatePhysics(dt);

  if (state.driveMode === "ragdoll") {
    state.interact.syncPickers();
  }

  // H4: static clouds today; hook for multi-frame point sequences later.
  state.pointLayers.update(state.clock.time);

  // Biomech overlay (track sample or realtime Newton–Euler estimate).
  updateBiomech(state, ts);

  const orbit = state.graph.controls;
  orbit.enabled = state.viewMode === "single" && !state.interact.isDragging();
  orbit.update();

  if (state.viewMode === "quad") {
    renderQuad(state);
  } else {
    state.graph.renderer.render(state.graph.scene, state.graph.camera);
  }

  if (state.forceFrames > 0) state.forceFrames -= 1;

  // While playing, push time at ~4 Hz; also fire once when auto-stop flips playing→false.
  const onTimeDue = ts - state.lastOnTimeTs >= 250;
  if (state.dotNet && (wasPlaying !== state.clock.playing || (state.clock.playing && onTimeDue))) {
    state.lastOnTimeTs = ts;
    reportTime(state);
  }

  if (needsFrame(state)) kick(state);
}

async function ensurePhysics(state: RuntimeState): Promise<PhysicsWorld | null> {
  if (state.disposed) return null;
  if (state.physics) return state.physics;
  if (!state.physicsInit) {
    const budgets = budgetsFor(state.tier);
    state.physicsInit = initRapier()
      .then((R) => {
        if (state.disposed) return;
        state.physics = new PhysicsWorld(R, { hz: budgets.physicsHz });
        state.physics.setFloor(0, 20);
        state.pose.setPhysics(state.physics, state.colliders);
      })
      .catch((err) => {
        console.error("acroHumanScene: Rapier init failed", err);
        state.dotNet
          ?.invokeMethodAsync(
            "OnError",
            err instanceof Error ? err.message : "Physics init failed",
          )
          .catch(() => {});
      });
  }
  await state.physicsInit;
  return state.physics;
}

function rebuildPhysicsBodies(state: RuntimeState, manifest: PackageManifest): void {
  if (!state.physics) return;
  const budgets = budgetsFor(state.tier);
  state.physics.setHz(budgets.physicsHz);

  const floor = manifest.environment?.floor;
  const floorY = floor?.y ?? 0;
  const floorSize = floor?.size ?? 20;
  state.physics.setFloor(floorY, floorSize);

  const g = manifest.environment?.gravity;
  if (g && g.length >= 3) {
    state.physics.setGravity(g[0], g[1], g[2]);
  } else {
    state.physics.setGravity(0, -9.81, 0);
  }

  const activeHumans = state.humans.slice(0, budgets.maxHumans);
  state.colliders.rebuild(state.physics, activeHumans, budgets.maxDynamic);
  state.pose.setPhysics(state.physics, state.colliders);
  // Re-apply drive mode so ragdoll/kinematic matches UI after rebuild.
  state.pose.setDriveMode(state.driveMode);
}

function attach(
  canvas: HTMLCanvasElement,
  dotNetRef?: DotNetBridge | null,
  options?: AttachOptions,
): void {
  if (!canvas) {
    throw new Error("acroHumanScene.attach: canvas element required");
  }

  const existing = handles.get(canvas);
  if (existing) {
    dispose(canvas);
  }

  const tier = resolveQualityTier(options?.tier);
  const budgets = budgetsFor(tier);
  const graph = createSceneGraph(canvas, budgets);
  const clock = new Clock();
  const anim = new AnimationPlayer();
  const pose = new PoseDriver();
  const interact = new PoseInteract(canvas, graph.camera, graph.scene);
  const colliders = new BoneColliders();
  const pointLayers = new PointLayers(graph.scene);
  pose.setInteract(interact);
  if (options?.resetPoseOnPlay != null) {
    pose.setResetPoseOnPlay(!!options.resetPoseOnPlay);
  }
  pose.setDriveMode(options?.driveMode ?? "animation");
  interact.attachListeners();

  const state: RuntimeState = {
    canvas,
    graph,
    clock,
    anim,
    pose,
    interact,
    physics: null,
    colliders,
    pointLayers,
    physicsInit: null,
    orbitActive: false,
    forceFrames: 1,
    lastOnTimeTs: 0,
    tier,
    representation: options?.representation ?? "solid",
    driveMode: options?.driveMode ?? "animation",
    humans: [],
    lastManifest: null,
    compareLayers: [],
    compareLayout: "overlay",
    cameraPreset: "iso",
    viewMode: "single",
    quadCameras: null,
    materialOriginals: new WeakMap(),
    clonedMaterials: new Set(),
    biomech: null,
    raf: 0,
    lastTs: 0,
    disposed: false,
    lastReportedPlaying: false,
    dotNet: dotNetRef ?? null,
    onResize: () => {
      if (!state.disposed) state.graph.resize();
    },
  };

  state.pointLayers.setRepresentation(state.representation);

  handles.set(canvas, state);
  window.addEventListener("resize", state.onResize);
  state.graph.controls.addEventListener("start", () => {
    state.orbitActive = true;
    kick(state);
  });
  state.graph.controls.addEventListener("end", () => {
    state.orbitActive = false;
    state.forceFrames = 2;
    kick(state);
  });
  state.graph.controls.addEventListener("change", () => {
    state.forceFrames = 1;
    kick(state);
  });
  canvas.addEventListener("pointerdown", () => {
    state.forceFrames = 1;
    kick(state);
  });
  kick(state);

  if (state.dotNet) {
    state.dotNet.invokeMethodAsync("OnTier", tier).catch(() => {});
  }
}

async function load(canvas: HTMLCanvasElement, packageManifestObject: PackageManifest): Promise<number> {
  const state = requireState(canvas);
  try {
    // Reset bookkeeping so re-load does not leak materials or resume old timeline.
    for (const mat of state.clonedMaterials) {
      mat.dispose();
    }
    state.clonedMaterials.clear();
    state.materialOriginals = new WeakMap();
    state.clock.time = 0;
    state.clock.playing = false;
    state.clock.duration = 0;
    state.humans = [];
    state.compareLayers = [];
    state.lastManifest = null;
    state.anim.setHumans([]);
    state.interact.setHumans([]);
    state.colliders.clear();
    state.pointLayers.clear();
    resetBiomech(state);

    const budgets = budgetsFor(state.tier);
    const result = await loadPackage(
      packageManifestObject,
      state.graph.humanRoot,
      state.tier,
      budgets.maxHumans,
    );
    state.humans = result.humans;
    state.lastManifest = packageManifestObject;
    state.anim.setHumans(result.humans);
    state.interact.setFocusHumanId(result.humans[0]?.def.id ?? null);
    state.interact.setHumans(result.humans);
    state.clock.duration = result.durationSeconds;
    state.clock.time = 0;
    state.clock.playing = false;
    if (packageManifestObject.timeline?.loop != null) {
      state.clock.loop = !!packageManifestObject.timeline.loop;
    }
    {
      const f = packageManifestObject.environment?.floor;
      state.graph.setFloor(f?.y ?? 0, f?.size ?? 20);
    }
    state.anim.snapToClock(state.clock);

    // H4 point layers (PLY dense + joints bin); failures are soft-skipped inside.
    await state.pointLayers.load(packageManifestObject.pointLayers, state.tier, budgets.maxPoints);
    if (state.disposed) return state.clock.duration;

    // Physics: await WASM if still loading, then build floor + capsules.
    await ensurePhysics(state);
    if (!state.disposed) {
      rebuildPhysicsBodies(state, packageManifestObject);
    }

    // Re-apply drive mode so pose pickers re-capture rest after load.
    state.pose.setDriveMode(state.driveMode);
    applyRepresentation(state, state.representation);
    state.dotNet?.invokeMethodAsync("OnReady").catch(() => {});
    reportTime(state);
    state.forceFrames = 1;
    kick(state);
    return state.clock.duration;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    state.dotNet?.invokeMethodAsync("OnError", message).catch(() => {});
    throw err;
  }
}

export interface CompareLayerInput {
  id: string;
  offsetSeconds?: number;
  opacity?: number;
  manifest: PackageManifest;
}

function disposeObject3D(root: THREE.Object3D): void {
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else if (mat) mat.dispose();
  });
}

function applyGroupOpacity(root: THREE.Object3D, opacity: number): void {
  const o = Math.min(1, Math.max(0, opacity));
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      mat.transparent = o < 0.999;
      mat.opacity = o;
      mat.needsUpdate = true;
    }
  });
}

function compareLayerGroups(state: RuntimeState): THREE.Object3D[] {
  return state.graph.humanRoot.children.filter((c) => c.name.startsWith("compare-layer:"));
}

function applyCompareLayout(state: RuntimeState): void {
  const groups = compareLayerGroups(state);
  const n = groups.length;
  if (n === 0) return;
  const aerial = state.compareLayout === "aerial";
  for (let i = 0; i < n; i++) {
    const group = groups[i];
    if (aerial) {
      const pos = instanceOffset(i, n);
      group.position.set(pos.x, 0, pos.z);
      applyGroupOpacity(group, 1);
    } else {
      group.position.set(0, 0, 0);
      applyGroupOpacity(group, i === 0 ? 1 : 0.78);
    }
  }
  const floorY = state.graph.floor.position.y;
  const size = aerial ? Math.max(20, aerialSpan(n) + 8) : 20;
  state.graph.setFloor(floorY, size);
}

function currentSpan(state: RuntimeState): number {
  const n = Math.max(1, compareLayerGroups(state).length);
  return state.compareLayout === "aerial" ? aerialSpan(n) : 4;
}

function applyPoseToCamera(
  camera: THREE.PerspectiveCamera,
  preset: CameraPreset,
  span: number,
): void {
  const pose = cameraPresetPose(preset, span);
  camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
  camera.lookAt(pose.target[0], pose.target[1], pose.target[2]);
}

function applyCameraPreset(state: RuntimeState, preset: CameraPreset): void {
  state.cameraPreset = preset;
  const span = currentSpan(state);
  const pose = cameraPresetPose(preset, span);
  state.graph.camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
  state.graph.controls.target.set(pose.target[0], pose.target[1], pose.target[2]);
  state.graph.controls.update();
  if (state.quadCameras) {
    applyPoseToCamera(state.quadCameras.aerial, "aerial", span);
    applyPoseToCamera(state.quadCameras.front, "front", span);
    applyPoseToCamera(state.quadCameras.side, "side", span);
    applyPoseToCamera(state.quadCameras.iso, "iso", span);
  }
  state.forceFrames = 1;
  kick(state);
}

function makeQuadCamera(src: THREE.PerspectiveCamera): THREE.PerspectiveCamera {
  const cam = new THREE.PerspectiveCamera(src.fov, src.aspect, src.near, src.far);
  cam.position.copy(src.position);
  return cam;
}

function ensureQuadCameras(state: RuntimeState): NonNullable<RuntimeState["quadCameras"]> {
  if (state.quadCameras) return state.quadCameras;
  const src = state.graph.camera;
  state.quadCameras = {
    aerial: makeQuadCamera(src),
    front: makeQuadCamera(src),
    side: makeQuadCamera(src),
    iso: makeQuadCamera(src),
  };
  const span = currentSpan(state);
  applyPoseToCamera(state.quadCameras.aerial, "aerial", span);
  applyPoseToCamera(state.quadCameras.front, "front", span);
  applyPoseToCamera(state.quadCameras.side, "side", span);
  applyPoseToCamera(state.quadCameras.iso, "iso", span);
  return state.quadCameras;
}

function renderQuad(state: RuntimeState): void {
  const renderer = state.graph.renderer;
  const scene = state.graph.scene;
  const cams = ensureQuadCameras(state);
  const width = renderer.domElement.width;
  const height = renderer.domElement.height;
  const hw = Math.max(1, Math.floor(width / 2));
  const hh = Math.max(1, Math.floor(height / 2));
  const aspect = hw / hh;
  const panes: { cam: THREE.PerspectiveCamera; x: number; y: number }[] = [
    { cam: cams.aerial, x: 0, y: hh },
    { cam: cams.front, x: hw, y: hh },
    { cam: cams.side, x: 0, y: 0 },
    { cam: cams.iso, x: hw, y: 0 },
  ];
  renderer.setScissorTest(true);
  for (const pane of panes) {
    pane.cam.aspect = aspect;
    pane.cam.updateProjectionMatrix();
    renderer.setViewport(pane.x, pane.y, hw, hh);
    renderer.setScissor(pane.x, pane.y, hw, hh);
    renderer.render(scene, pane.cam);
  }
  renderer.setScissorTest(false);
  renderer.setViewport(0, 0, width, height);
}

function setCompareLayout(canvas: HTMLCanvasElement, layout: string): void {
  const state = requireState(canvas);
  state.compareLayout = layout === "overlay" ? "overlay" : "aerial";
  applyCompareLayout(state);
  // Layers moved: filter history / derivative windows no longer describe the scene.
  if (state.biomech) {
    resetBiomechEstimators(state.biomech);
    state.forceFrames = Math.max(state.forceFrames, 2);
  }
  applyCameraPreset(state, state.cameraPreset);
}

function setCameraPreset(canvas: HTMLCanvasElement, preset: string): void {
  const state = requireState(canvas);
  const next: CameraPreset =
    preset === "aerial" || preset === "front" || preset === "side" || preset === "iso"
      ? preset
      : "iso";
  applyCameraPreset(state, next);
}

function setViewMode(canvas: HTMLCanvasElement, mode: string): void {
  const state = requireState(canvas);
  state.viewMode = mode === "quad" ? "quad" : "single";
  if (state.viewMode === "quad") ensureQuadCameras(state);
  state.forceFrames = 1;
  kick(state);
}

function sharedLayerDuration(layers: { duration: number; offset: number }[]): number {
  let max = 0;
  for (const layer of layers) {
    if (layer.duration <= 0) continue;
    max = Math.max(max, Math.max(0, layer.duration - layer.offset));
  }
  return max;
}

function clearHumanRoot(state: RuntimeState): void {
  const parent = state.graph.humanRoot;
  while (parent.children.length > 0) {
    const child = parent.children[0];
    parent.remove(child);
    disposeObject3D(child);
  }
}

/** Load N packages into one scene (shared camera). Same package may be loaded twice. */
async function loadLayers(canvas: HTMLCanvasElement, layers: CompareLayerInput[]): Promise<number> {
  const state = requireState(canvas);
  try {
    for (const mat of state.clonedMaterials) mat.dispose();
    state.clonedMaterials.clear();
    state.materialOriginals = new WeakMap();
    state.clock.time = 0;
    state.clock.playing = false;
    state.clock.loop = false;
    state.humans = [];
    state.compareLayers = [];
    state.lastManifest = layers[0]?.manifest ?? null;
    state.anim.setHumans([]);
    state.interact.setHumans([]);
    state.colliders.clear();
    state.pointLayers.clear();
    resetBiomech(state);
    clearHumanRoot(state);

    const list = Array.isArray(layers) ? layers.slice(0, 6) : [];
    const budgets = budgetsFor(state.tier);
    const perLayerCap = Math.max(1, Math.min(4, budgets.maxHumans));

    for (let i = 0; i < list.length; i++) {
      const layer = list[i];
      if (!layer?.manifest || !layer.id) continue;
      const group = new THREE.Group();
      group.name = `compare-layer:${layer.id}`;
      state.graph.humanRoot.add(group);
      const result = await loadPackage(layer.manifest, group, state.tier, perLayerCap, { clear: false });
      if (state.disposed) return 0;
      const offset = Number(layer.offsetSeconds) || 0;
      const opacity = layer.opacity == null ? (i === 0 ? 1 : 0.78) : Number(layer.opacity);
      if (opacity < 0.999) applyGroupOpacity(group, opacity);
      for (const human of result.humans) {
        human.layerId = layer.id;
        human.timeOffset = offset;
      }
      state.humans.push(...result.humans);
      state.compareLayers.push({ id: layer.id, offset, duration: result.durationSeconds });
      if (i === 0) {
        const f = layer.manifest.environment?.floor;
        state.graph.setFloor(f?.y ?? 0, f?.size ?? 20);
      }
    }

    state.clock.duration = sharedLayerDuration(state.compareLayers);
    state.clock.time = 0;
    state.clock.playing = false;
    state.anim.setHumans(state.humans);
    state.interact.setFocusHumanId(state.humans[0]?.def.id ?? null);
    state.interact.setHumans(state.humans);
    state.driveMode = "animation";
    state.pose.setDriveMode("animation");
    applyRepresentation(state, state.representation);
    state.anim.snapToClock(state.clock);
    state.compareLayout = "aerial";
    state.cameraPreset = "aerial";
    applyCompareLayout(state);
    applyCameraPreset(state, "aerial");
    state.dotNet?.invokeMethodAsync("OnReady").catch(() => {});
    reportTime(state);
    return state.clock.duration;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    state.dotNet?.invokeMethodAsync("OnError", message).catch(() => {});
    throw err;
  }
}

function setLayerOffset(canvas: HTMLCanvasElement, layerId: string, offsetSeconds: number): number {
  const state = requireState(canvas);
  const offset = Number(offsetSeconds) || 0;
  for (const human of state.humans) {
    if (human.layerId === layerId) human.timeOffset = offset;
  }
  for (const layer of state.compareLayers) {
    if (layer.id === layerId) layer.offset = offset;
  }
  state.clock.duration = sharedLayerDuration(state.compareLayers);
  if (!state.pose.ownsBones()) state.anim.snapToClock(state.clock);
  reportTime(state);
  return state.clock.duration;
}

function setLayerOpacity(canvas: HTMLCanvasElement, layerId: string, opacity: number): void {
  const state = requireState(canvas);
  const group = state.graph.humanRoot.children.find((c) => c.name === `compare-layer:${layerId}`);
  if (group) applyGroupOpacity(group, opacity);
}

function pickRecorderMime(): string {
  const types = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
  if (typeof MediaRecorder === "undefined") return "";
  return types.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
}

/** Play the shared clock from 0 to end while recording the current camera view. Returns a blob: URL. */
async function recordUntilEnd(canvas: HTMLCanvasElement): Promise<string> {
  const state = requireState(canvas);
  const capture = (canvas as HTMLCanvasElement & { captureStream?: (fps?: number) => MediaStream }).captureStream;
  if (typeof capture !== "function") {
    throw new Error("This browser cannot record the 3D canvas");
  }
  const mime = pickRecorderMime();
  if (!mime) {
    throw new Error("MediaRecorder WebM is not supported in this browser");
  }

  state.clock.loop = false;
  state.clock.seek(0);
  if (!state.pose.ownsBones()) state.anim.snapToClock(state.clock);

  const stream = capture.call(canvas, 30);
  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 3_500_000 });
  const chunks: BlobPart[] = [];
  rec.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };
  const stopped = new Promise<Blob>((resolve, reject) => {
    rec.onstop = () => resolve(new Blob(chunks, { type: mime.split(";")[0] }));
    rec.onerror = () => reject(new Error("3D view recording failed"));
  });
  rec.start(100);

  if (state.clock.duration <= 0.05) {
    await new Promise((r) => setTimeout(r, 1500));
  } else {
    state.clock.playing = true;
    state.anim.play();
    reportTime(state);
    await new Promise<void>((resolve) => {
      const check = () => {
        if (state.disposed || !state.clock.playing) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  if (rec.state !== "inactive") rec.stop();
  const blob = await stopped;
  for (const track of stream.getTracks()) track.stop();
  return URL.createObjectURL(blob);
}

function play(canvas: HTMLCanvasElement): void {
  const state = requireState(canvas);
  // Play while in pose → leave pose so the clip (or reset) can drive bones.
  // Ragdoll keeps ownership (physics continues).
  if (state.driveMode === "pose" || state.pose.mode === "pose") {
    state.driveMode = "animation";
    state.pose.setDriveMode("animation");
  }
  // resetPoseOnPlay: restore clip pose after interactive posing.
  state.pose.onPlay(state.anim, state.clock);
  state.clock.playing = true;
  state.anim.play();
  reportTime(state);
  kick(state);
}

function pause(canvas: HTMLCanvasElement): void {
  const state = requireState(canvas);
  state.clock.playing = false;
  state.anim.pause();
  reportTime(state);
  state.forceFrames = 1;
  kick(state);
}

function seek(canvas: HTMLCanvasElement, seconds: number): void {
  const state = requireState(canvas);
  state.clock.seek(seconds);
  // Absolute snap in animation mode; pose/ragdoll keep user/physics joints.
  if (!state.pose.ownsBones()) {
    state.pose.onSeek(state.anim, state.clock);
  }
  state.forceFrames = 1;
  kick(state);
}

function setLoop(canvas: HTMLCanvasElement, loop: boolean): void {
  requireState(canvas).clock.loop = !!loop;
}

function setSpeed(canvas: HTMLCanvasElement, speed: number): void {
  const state = requireState(canvas);
  state.clock.speed = Number.isFinite(speed) ? Math.max(0, speed) : 1;
}

function setRepresentation(canvas: HTMLCanvasElement, mode: RepresentationMode): void {
  const state = requireState(canvas);
  applyRepresentation(state, mode);
  state.forceFrames = 1;
  kick(state);
}

function setDriveMode(canvas: HTMLCanvasElement, mode: DriveMode): void {
  const state = requireState(canvas);
  // Entering pose: pause timeline so mixers do not fight the drag.
  if (mode === "pose" && state.clock.playing) {
    state.clock.playing = false;
    state.anim.pause();
    reportTime(state);
  }
  // Global drive mode for all humans (H3); per-human driveModeByHuman is later.
  state.driveMode = mode;
  state.pose.setDriveMode(mode);
  if (mode === "pose") {
    state.interact.syncPickers();
  }
  state.forceFrames = 1;
  kick(state);
}

/** Focus pose picks / future per-human tools on one human id (multi-human). */
function setFocusHuman(canvas: HTMLCanvasElement, humanId: string | null): void {
  const state = requireState(canvas);
  const id =
    humanId && state.humans.some((h) => h.def.id === humanId)
      ? humanId
      : state.humans[0]?.def.id ?? null;
  state.interact.setFocusHumanId(id);
}

function setQualityTier(canvas: HTMLCanvasElement, tier: QualityTier | "auto"): void {
  const state = requireState(canvas);
  if (tier === "auto") {
    // Clear override so next attach also re-detects from hardware.
    writeStoredTier(null);
    state.tier = resolveQualityTier("auto");
  } else {
    state.tier = tier;
    // H7: persist user override so next attach resolves the same tier.
    writeStoredTier(tier);
  }
  const budgets = budgetsFor(state.tier);
  state.graph.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, budgets.maxPixelRatio));
  // H4/H7: re-stride dense clouds + re-cap maxPoints for the new tier without re-fetch.
  state.pointLayers.setTier(state.tier, budgets.maxPoints);
  // Hide humans beyond tier maxHumans (loaded on a higher tier).
  state.humans.forEach((h, i) => {
    h.root.visible = i < budgets.maxHumans;
  });
  if (state.physics) {
    state.physics.setHz(budgets.physicsHz);
    // Rebuild so maxDynamic tier gate (low = 1 ragdoll) reapplies on visible set.
    if (state.lastManifest && state.humans.length > 0) {
      rebuildPhysicsBodies(state, state.lastManifest);
    }
  }
  state.dotNet?.invokeMethodAsync("OnTier", state.tier).catch(() => {});
}

/** Show/hide a loaded point layer by manifest id (dense or joints). */
function setPointLayerVisible(canvas: HTMLCanvasElement, layerId: string, visible: boolean): void {
  requireState(canvas).pointLayers.setVisible(layerId, visible);
}

/** Layer ids currently in the scene (after load). */
function listPointLayers(canvas: HTMLCanvasElement): { id: string; kind: string; visible: boolean }[] {
  return requireState(canvas).pointLayers.list();
}

function dispose(canvas: HTMLCanvasElement): void {
  const state = handles.get(canvas);
  if (!state || state.disposed) return;
  state.disposed = true;
  cancelAnimationFrame(state.raf);
  window.removeEventListener("resize", state.onResize);
  state.interact.dispose();
  state.colliders.dispose();
  state.pointLayers.dispose();
  state.biomech?.mujoco.dispose();
  state.biomech?.overlay.dispose();
  state.biomech = null;
  state.physics?.dispose();
  state.physics = null;
  for (const mat of state.clonedMaterials) {
    mat.dispose();
  }
  state.clonedMaterials.clear();
  state.graph.dispose();
  handles.delete(canvas);
}

/** Force WebGL viewport resize (e.g. after fullscreen toggle). */
function resize(canvas: HTMLCanvasElement): void {
  const state = handles.get(canvas);
  if (!state || state.disposed) return;
  state.graph.resize();
}

function exportPose(canvas: HTMLCanvasElement): ExportedPose {
  return exportHumansPose(requireState(canvas).humans);
}

function applyPose(canvas: HTMLCanvasElement, pose: ExportedPose): void {
  const state = requireState(canvas);
  applyExportedPose(state.humans, pose);
  state.pose.markPoseDirty();
  state.forceFrames = Math.max(state.forceFrames, 3);
  kick(state);
}

/**
 * Toggle Fullscreen API on the viewer root element.
 * Returns true when entering fullscreen, false when exiting.
 * Listens once for exit (Esc) and notifies DotNet via OnFullscreenChange when attached.
 */
function toggleFullscreen(
  element: HTMLElement,
  canvas?: HTMLCanvasElement,
  dotNetRef?: { invokeMethodAsync: (name: string, ...args: unknown[]) => Promise<unknown> } | null,
): boolean {
  if (!element) {
    throw new Error("acroHumanScene.toggleFullscreen: element required");
  }

  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
  };
  const el = element as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
  };

  const active =
    document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;

  if (active === element || (active && element.contains(active))) {
    const exit = document.exitFullscreen?.bind(document) ?? doc.webkitExitFullscreen?.bind(doc);
    void Promise.resolve(exit?.()).then(() => {
      if (canvas) resize(canvas);
      void dotNetRef?.invokeMethodAsync("OnFullscreenChange", false);
    });
    return false;
  }

  const request =
    element.requestFullscreen?.bind(element) ?? el.webkitRequestFullscreen?.bind(el);
  void Promise.resolve(request?.()).then(() => {
    if (canvas) resize(canvas);
    void dotNetRef?.invokeMethodAsync("OnFullscreenChange", true);
  });

  const onFsChange = () => {
    const now =
      document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
    if (now !== element && !(now && element.contains(now))) {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      if (canvas) resize(canvas);
      void dotNetRef?.invokeMethodAsync("OnFullscreenChange", false);
    }
  };
  document.addEventListener("fullscreenchange", onFsChange);
  document.addEventListener("webkitfullscreenchange", onFsChange);

  return true;
}

export const acroHumanScene = {
  attach,
  load,
  loadLayers,
  setCompareLayout,
  setCameraPreset,
  setViewMode,
  setLayerOffset,
  setLayerOpacity,
  recordUntilEnd,
  play,
  pause,
  seek,
  setLoop,
  setSpeed,
  setRepresentation,
  setDriveMode,
  setFocusHuman,
  setQualityTier,
  setPointLayerVisible,
  listPointLayers,
  resize,
  toggleFullscreen,
  exportPose,
  applyPose,
  setForceOverlay,
  getForceOverlayState,
  sampleBiomechPoseTrack,
  sampleBiomechPoseTrackJson,
  dispose,
};

// Also export as default for the IIFE globalName fallback.
export default acroHumanScene;
